import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
  ReactNode,
} from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../config/firebase';
import { useAuth } from './AuthContext';
import { StartupProfile, InvestorProfile } from '../types/firestore';
import { reportAccessFailure } from '../utils/reportAccessFailure';
import { callWithRetry } from '../utils/callableRetry';
import { isCurrentProfileResponse } from '../utils/signupSessionGuards';

// Profile context types
interface ProfileContextType {
  // State
  startupProfile: StartupProfile | null;
  investorProfile: InvestorProfile | null;
  loading: boolean;
  /** True after we have attempted to load the current user's profile (so we know null = no profile, not "not loaded yet") */
  profileLoadAttempted: boolean;
  error: string | null;

  // Functions
  loadProfile: (userId: string, userType: 'startup' | 'investor') => Promise<void>;
  createProfile: (
    userId: string,
    userType: 'startup' | 'investor',
    initialData?: any
  ) => Promise<void>;
  updateProfile: (
    userId: string,
    userType: 'startup' | 'investor',
    updates: any,
    stepNumber?: number
  ) => Promise<void>;
  reloadProfile: () => Promise<void>;
  clearError: () => void;
}

// Create context
const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

// Provider component
interface ProfileProviderProps {
  children: ReactNode;
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  // State
  const [startupProfile, setStartupProfile] = useState<StartupProfile | null>(null);
  const [investorProfile, setInvestorProfile] = useState<InvestorProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [profileLoadAttempted, setProfileLoadAttempted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tracks which uid we've already started a profile fetch for — prevents double-fetching
  // when both the Firebase auth listener (early path) and the user watcher both fire.
  const profileFetchedForUid = useRef<string | null>(null);

  // Get current user from auth context
  const { user } = useAuth();

  // Load profile function
  const profileRequestUid = useRef<string | null>(null);

  const loadProfile = useCallback(async (userId: string, userType: 'startup' | 'investor') => {
    profileRequestUid.current = userId;
    try {
      setError(null);
      setLoading(true);

      if (userType === 'startup') {
        const getStartupProfile = httpsCallable(functions, 'getStartupProfile');
        const result = await callWithRetry(() => getStartupProfile({ userId }));
        const data = result.data as any;
        if (!isCurrentProfileResponse(profileRequestUid.current, userId)) return;

        if (data.success) {
          setStartupProfile(data.profile);
        } else {
          // Profile doesn't exist yet, that's ok
          setStartupProfile(null);
        }
      } else if (userType === 'investor') {
        const getInvestorProfile = httpsCallable(functions, 'getInvestorProfile');
        const result = await callWithRetry(() => getInvestorProfile({ userId }));
        const data = result.data as any;
        if (!isCurrentProfileResponse(profileRequestUid.current, userId)) return;

        if (data.success) {
          setInvestorProfile(data.profile);
        } else {
          // Profile doesn't exist yet, that's ok
          setInvestorProfile(null);
        }
      }
    } catch (error: any) {
      if (!isCurrentProfileResponse(profileRequestUid.current, userId)) return;
      const message = error?.message || 'Failed to load your profile. Please try again.';
      console.error('Error loading profile:', error);
      setError(message);
      reportAccessFailure({
        kind: 'profile_load_failed',
        route: window.location.pathname,
        message,
      });
      setStartupProfile(null);
      setInvestorProfile(null);
    } finally {
      if (isCurrentProfileResponse(profileRequestUid.current, userId)) {
        setLoading(false);
        setProfileLoadAttempted(true);
      }
    }
  }, []);

  // Create profile function
  const createProfile = useCallback(
    async (userId: string, userType: 'startup' | 'investor', initialData?: any) => {
      try {
        setError(null);
        // Do NOT set loading=true here — that would block the URL-sync effect in ProfileWizard
        // and prevent navigation to the next step after save.

        if (userType === 'startup') {
          const createStartupProfile = httpsCallable(functions, 'createStartupProfile');
          const result = await createStartupProfile({ userId, initialData });
          const data = result.data as any;

          if (data.success) {
            setStartupProfile(data.profile);
          } else {
            throw new Error(data.error || 'Failed to create startup profile');
          }
        } else if (userType === 'investor') {
          const createInvestorProfile = httpsCallable(functions, 'createInvestorProfile');
          const result = await createInvestorProfile({ userId, initialData });
          const data = result.data as any;

          if (data.success) {
            setInvestorProfile(data.profile);
          } else {
            throw new Error(data.error || 'Failed to create investor profile');
          }
        }
      } catch (error: any) {
        setError(error.message);
        throw error;
      }
    },
    []
  );

  // Update profile function
  const updateProfile = useCallback(
    async (userId: string, userType: 'startup' | 'investor', updates: any, stepNumber?: number) => {
      try {
        setError(null);
        // Do NOT set loading=true here — that would block the URL-sync effect in ProfileWizard
        // and prevent navigation to the next step after save.

        if (userType === 'startup') {
          const updateStartupProfile = httpsCallable(functions, 'updateStartupProfile');
          const result = await updateStartupProfile({ userId, updates, stepNumber });
          const data = result.data as any;

          if (data.success) {
            setStartupProfile(data.profile);
          } else {
            throw new Error(data.error || 'Failed to update startup profile');
          }
        } else if (userType === 'investor') {
          const updateInvestorProfile = httpsCallable(functions, 'updateInvestorProfile');
          const result = await updateInvestorProfile({ userId, updates, stepNumber });
          const data = result.data as any;

          if (data.success) {
            setInvestorProfile(data.profile);
          } else {
            throw new Error(data.error || 'Failed to update investor profile');
          }
        }
      } catch (error: any) {
        setError(error.message);
        throw error;
      }
    },
    []
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const reloadProfile = useCallback(async () => {
    if (!user) return;
    profileFetchedForUid.current = null;
    setProfileLoadAttempted(false);
    setError(null);
    await loadProfile(user.id, user.userType);
  }, [user, loadProfile]);

  // Early profile fetch: listens to Firebase auth directly and uses the userType cached in
  // localStorage by AuthContext. On returning visits, this fires at the same time as the
  // DB user fetch in AuthContext — the two run in parallel instead of serially.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, fb => {
      if (!fb) {
        profileFetchedForUid.current = null;
        setProfileLoadAttempted(false);
        setStartupProfile(null);
        setInvestorProfile(null);
        setError(null);
        return;
      }

      let cachedUserType: 'startup' | 'investor' | null = null;
      try {
        cachedUserType = localStorage.getItem(`prairi_ut_${fb.uid}`) as
          | 'startup'
          | 'investor'
          | null;
      } catch (_e) {
        // localStorage unavailable — fall back to serial load via user watcher
      }

      if (profileFetchedForUid.current === fb.uid) return;

      // Another account's profile must leave the screen before the new one arrives.
      setStartupProfile(null);
      setInvestorProfile(null);
      if (cachedUserType) {
        profileFetchedForUid.current = fb.uid;
        setProfileLoadAttempted(false);
        setLoading(true);
        loadProfile(fb.uid, cachedUserType);
      } else {
        profileFetchedForUid.current = null;
        profileRequestUid.current = null;
      }
    });
    return unsubscribe;
  }, [loadProfile]);

  // Fallback: if there's no cached userType (first-ever login), start profile fetch once the
  // DB user is available. Also handles user switching.
  useEffect(() => {
    if (!user) return;
    if (profileFetchedForUid.current === user.id) return; // already fetching via early path

    profileFetchedForUid.current = user.id;
    setProfileLoadAttempted(false);
    setLoading(true);
    setStartupProfile(null);
    setInvestorProfile(null);
    loadProfile(user.id, user.userType);
  }, [user, loadProfile]);

  // Memoized context value — consumers only re-render when a field they use actually changes
  const value = useMemo<ProfileContextType>(
    () => ({
      startupProfile,
      investorProfile,
      loading,
      profileLoadAttempted,
      error,
      loadProfile,
      createProfile,
      updateProfile,
      reloadProfile,
      clearError,
    }),
    [
      startupProfile,
      investorProfile,
      loading,
      profileLoadAttempted,
      error,
      loadProfile,
      createProfile,
      updateProfile,
      reloadProfile,
      clearError,
    ]
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

// Hook to use profile context
export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};
